import React from "react";
import {
  Activity, AlertTriangle, CheckCircle2, Info, CalendarClock,
  PiggyBank, Wallet, ArrowRight, Settings2, Lock, ChevronDown, Scale,
} from "lucide-react";
import { HealthScoreRing } from "@/components/HealthScoreRing";
import { formatCompact } from "@/lib/currency";
import { classifyScore, SCORE_LEGEND, emergencyBasisLabel } from "@/lib/financeMetrics";

const BAND_TXT = { sehat: "text-emerald-600", waspada: "text-amber-600", kritis: "text-rose-600" };
function subLabel(v) {
  const c = classifyScore(v);
  return { label: c.label, color: BAND_TXT[c.key] };
}

const LEVEL = {
  danger: { wrap: "border-rose-100 bg-rose-50", icon: "text-rose-500", Icon: AlertTriangle, title: "text-rose-700" },
  warning: { wrap: "border-amber-100 bg-amber-50", icon: "text-amber-500", Icon: AlertTriangle, title: "text-amber-700" },
  positive: { wrap: "border-emerald-100 bg-emerald-50", icon: "text-emerald-600", Icon: CheckCircle2, title: "text-emerald-700" },
  info: { wrap: "border-zinc-100 bg-zinc-50", icon: "text-zinc-400", Icon: Info, title: "text-zinc-700" },
};

function WarningCard({ w }) {
  const s = LEVEL[w.level] || LEVEL.info;
  const Icon = s.Icon;
  return (
    <div className={`flex items-start gap-3 rounded-2xl border p-4 ${s.wrap}`} data-testid={`warning-${w.id}`}>
      <Icon className={`mt-0.5 h-4.5 w-4.5 shrink-0 ${s.icon}`} />
      <div className="min-w-0">
        <p className={`font-heading text-sm font-bold ${s.title}`}>{w.title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-zinc-600">{w.message}</p>
      </div>
    </div>
  );
}

function LearningCard({ health, onSetup }) {
  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-6 text-center shield-shadow" data-testid="health-learning">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-100">
        <Activity className="h-6 w-6 text-zinc-400" />
      </div>
      <p className="mt-3 font-heading text-base font-bold text-zinc-900">CeKKeU masih mempelajari pola keuangan Anda</p>
      <p className="mt-1 text-sm text-zinc-500">
        Skor kesehatan harian & prakiraan akan tampil setelah data cukup. Lengkapi ini dulu:
      </p>
      <ul className="mx-auto mt-4 max-w-xs space-y-2 text-left">
        {health.need.map((n, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-zinc-700" data-testid={`health-need-${i}`}>
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
            {n}
          </li>
        ))}
      </ul>
      {onSetup && (
        <button
          data-testid="health-open-setup"
          onClick={onSetup}
          className="mt-5 inline-flex h-11 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm font-semibold text-white"
        >
          <Settings2 className="h-4 w-4" /> Pengaturan Keuangan
        </button>
      )}
    </div>
  );
}

// Full panel for the Analisis tab.
export function DailyHealthPanel({ health, onSetup }) {
  if (!health) return null;
  if (health.status === "learning") return <LearningCard health={health} onSetup={onSetup} />;

  const { score, forecast, spending, monthly, actual, deficitSinceISO, deficitMode } = health;
  const endPositive = forecast.projectedEndBalance >= 0;
  const scoreCls = classifyScore(score.value);
  const runway = forecast.runway || {};

  return (
    <div className="space-y-4" data-testid="daily-health-panel">
      {/* Score */}
      <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid="health-score-card">
        <div className="flex items-center gap-5">
          <HealthScoreRing value={score.value} color={score.color} label={score.label} />
          <div className="min-w-0">
            <p className="font-heading text-sm font-bold text-zinc-900">Skor Kesehatan Harian</p>
            <p className="mt-0.5 font-mono text-lg font-black text-zinc-900" data-testid="health-score-value">
              {score.value}/100 — <span className={BAND_TXT[scoreCls.key]}>{score.label.toUpperCase()}</span>
            </p>
            <p className="mt-0.5 text-[11px] text-zinc-400">
              Semakin tinggi semakin sehat (0 = terburuk, 100 = terbaik). Terpisah dari Skor Ketahanan.
            </p>
            {health.baselineLearning && (
              <p className="mt-2 inline-block rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-medium text-zinc-500" data-testid="baseline-learning-badge">
                Perlu lebih banyak data harian untuk skor yang lebih akurat
              </p>
            )}
          </div>
        </div>
        {/* Sub-scores — each shows its scale + band so nothing is ambiguous */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          {[
            { k: "cashflow", label: "Arus kas" },
            { k: "pace", label: "Kecepatan belanja" },
            { k: "buffer", label: "Dana darurat" },
          ].map((s) => {
            const v = score.components[s.k];
            const sl = subLabel(v);
            return (
              <div key={s.k} className="rounded-2xl bg-zinc-50 p-3 text-center" data-testid={`subscore-${s.k}`}>
                <p className="font-mono text-lg font-black text-zinc-900">{v}<span className="text-xs font-bold text-zinc-400">/100</span></p>
                <p className={`text-[10px] font-bold uppercase ${sl.color}`}>{sl.label}</p>
                <p className="mt-0.5 text-[10px] leading-tight text-zinc-500">{s.label}</p>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-center text-[10px] text-zinc-400" data-testid="score-legend">{SCORE_LEGEND}</p>

        {/* Expandable — why this score */}
        <details className="mt-3 rounded-2xl bg-zinc-50 p-3" data-testid="score-why">
          <summary className="flex cursor-pointer items-center justify-between text-xs font-semibold text-zinc-700">
            <span>Mengapa skor saya {score.value}?</span>
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          </summary>
          <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-zinc-600" data-testid="subscore-explain">
            <p>Arus kas ({score.components.cashflow}/100, bobot 45%): {score.components.cashflow >= 60 ? "pemasukan masih menutupi pengeluaran bulan ini" : score.components.cashflow >= 30 ? "pengeluaran mulai menekan pemasukan bulan ini" : "pengeluaran bulan ini sudah melebihi pemasukan"}.</p>
            <p>Kecepatan belanja ({score.components.pace}/100, bobot 30%): {score.components.pace >= 70 ? "belanja masih dalam pola normal Anda" : score.components.pace >= 40 ? "belanja sedikit lebih cepat dari biasanya" : "belanja jauh lebih cepat dari biasanya"}.</p>
            <p>Dana darurat ({score.components.buffer}/100, bobot 25%): {score.components.buffer >= 70 ? "dana darurat masih tergolong sehat" : score.components.buffer >= 40 ? "dana darurat cukup tapi perlu diperkuat" : "dana darurat masih tipis"}.</p>
            <p className="pt-1 text-zinc-400">Skor akhir = 45% arus kas + 30% kecepatan belanja + 25% dana darurat. Komponen dengan angka terendah paling menurunkan skor Anda.</p>
          </div>
        </details>
      </div>

      {/* KONDISI SAAT INI — actual recorded figures only (fact, not forecast) */}
      <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid="health-actual-card">
        <div className="mb-4 flex items-center gap-2">
          <Scale className="h-4 w-4 text-zinc-400" />
          <p className="font-heading text-sm font-bold text-zinc-900">Kondisi Saat Ini</p>
          <span className="ml-auto text-[10px] text-zinc-400">bulan berjalan · tercatat</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-2xl bg-emerald-50 p-3">
            <p className="text-[10px] font-semibold text-emerald-700">Pemasukan tercatat</p>
            <p className="mt-1 whitespace-nowrap font-mono text-sm font-bold text-emerald-700" data-testid="actual-income">{formatCompact(actual.income)}</p>
          </div>
          <div className="rounded-2xl bg-rose-50 p-3">
            <p className="text-[10px] font-semibold text-rose-600">Pengeluaran tercatat</p>
            <p className="mt-1 whitespace-nowrap font-mono text-sm font-bold text-rose-600" data-testid="actual-expense">{formatCompact(actual.expense)}</p>
          </div>
          <div className={`rounded-2xl p-3 ${actual.net >= 0 ? "bg-zinc-50" : "bg-rose-100"}`}>
            <p className="text-[10px] font-semibold text-zinc-500">{actual.net >= 0 ? "Surplus aktual" : "Defisit aktual"}</p>
            <p className={`mt-1 whitespace-nowrap font-mono text-sm font-bold ${actual.net >= 0 ? "text-zinc-900" : "text-rose-700"}`} data-testid="actual-net">
              {formatCompact(Math.abs(actual.net))}
            </p>
          </div>
        </div>
        {actual.net < 0 && deficitSinceISO && (
          <p className="mt-3 text-xs leading-relaxed text-rose-700" data-testid="actual-deficit-since">
            Total pengeluaran sudah melebihi pemasukan bulan ini. Selisih ini ditutup dari saldo sebelumnya, tabungan, dana darurat, atau sumber dana lain.
          </p>
        )}
      </div>

      {/* PROYEKSI AKHIR BULAN — forecast (clearly labelled) */}
      <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid="health-forecast-card">
        <div className="mb-4 flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-zinc-400" />
          <p className="font-heading text-sm font-bold text-zinc-900">Proyeksi Akhir Bulan</p>
          <span className="ml-auto text-[10px] text-zinc-400">perkiraan</span>
        </div>
        <div className="rounded-2xl bg-zinc-900 p-5 text-white">
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-heading">
            {endPositive ? "Perkiraan sisa uang akhir bulan" : "Proyeksi defisit akhir bulan"}
          </p>
          <p
            className={`mt-1 font-mono text-3xl font-black ${endPositive ? "text-emerald-400" : "text-rose-400"}`}
            data-testid="forecast-end-balance"
          >
            {formatCompact(Math.abs(forecast.projectedEndBalance))}
          </p>
          {!endPositive && actual.net < 0 ? (
            <p className="mt-1 text-xs text-rose-300" data-testid="forecast-note">
              Arus kas bulan ini sudah defisit — angka di atas adalah proyeksi kekurangan bila pola berlanjut.
            </p>
          ) : forecast.shortfallDay ? (
            <p className="mt-1 text-xs text-rose-300" data-testid="forecast-shortfall">
              Prakiraan: uang bisa mulai tidak cukup sekitar tanggal {forecast.shortfallDay}.
            </p>
          ) : (
            <p className="mt-1 text-xs text-zinc-400">Sisa {spending.daysRemaining} hari lagi di bulan ini.</p>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-zinc-100 bg-white p-4">
            <div className="flex items-center gap-1.5 text-zinc-500">
              <Wallet className="h-3.5 w-3.5" />
              <span className="text-[11px] font-semibold">Anggaran aman / hari</span>
            </div>
            <p className="mt-1 font-mono text-lg font-bold text-emerald-600" data-testid="forecast-safe-per-day">
              {formatCompact(forecast.safePerDay)}
            </p>
            {deficitMode && (
              <p className="mt-0.5 text-[10px] leading-tight text-rose-600" data-testid="safe-deficit-note">
                Arus kas sudah defisit — hindari pengeluaran non-wajib dulu.
              </p>
            )}
          </div>
          <div className="rounded-2xl border border-zinc-100 bg-white p-4">
            <div className="flex items-center gap-1.5 text-zinc-500">
              <PiggyBank className="h-3.5 w-3.5" />
              <span className="text-[11px] font-semibold">Dana darurat menutup</span>
            </div>
            {runway.available ? (
              <>
                <p className="mt-1 font-mono text-lg font-bold text-zinc-900" data-testid="forecast-emergency-months">
                  {runway.months >= 6 ? "6+" : runway.months.toFixed(1).replace(".", ",")} bln
                </p>
                <p className="mt-0.5 text-[10px] leading-tight text-zinc-400">{emergencyBasisLabel(runway.basis)}</p>
              </>
            ) : (
              <p className="mt-1 font-mono text-sm font-bold text-zinc-400" data-testid="forecast-emergency-months">
                Belum tersedia
              </p>
            )}
          </div>
        </div>
        {!monthly.hasConfiguredIncome && (
          <div className="mt-3 flex items-start gap-2 rounded-2xl bg-amber-50 p-3" data-testid="forecast-setup-hint">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <p className="text-xs leading-relaxed text-amber-700">
              Proyeksi awal — akurasi akan meningkat setelah tersedia lebih banyak riwayat transaksi dan penghasilan bulanan disetel.
              {onSetup && (
                <button onClick={onSetup} className="ml-1 font-semibold underline" data-testid="forecast-setup-link">
                  Lengkapi Pengaturan Keuangan
                </button>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Warnings & insights */}
      <div className="space-y-2" data-testid="health-warnings">
        {health.warnings.map((w) => (
          <WarningCard key={w.id} w={w} />
        ))}
      </div>

      <div className="flex items-start gap-2 rounded-2xl bg-emerald-50 p-3">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        <p className="text-xs leading-relaxed text-emerald-700">
          Semua angka & prakiraan dihitung di perangkat Anda. Tidak ada nominal yang dikirim ke server CeKKeU.
        </p>
      </div>
    </div>
  );
}

// Compact card for the Beranda dashboard.
export function DailyHealthCompact({ health, onOpen }) {
  if (!health) return null;
  const learning = health.status === "learning";
  const score = health.score;
  const end = health.forecast?.projectedEndBalance;

  return (
    <button
      data-testid="health-compact-card"
      onClick={onOpen}
      className="mt-4 flex w-full items-center gap-4 rounded-3xl border border-zinc-200 bg-white p-5 text-left shield-shadow transition-transform active:scale-[0.99]"
    >
      {learning ? (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-zinc-100">
          <Activity className="h-7 w-7 text-zinc-400" />
        </div>
      ) : (
        <HealthScoreRing value={score.value} color={score.color} size={72} stroke={8} />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-heading text-sm font-bold text-zinc-900">Kesehatan Harian</p>
        {learning ? (
          <p className="mt-0.5 text-xs text-zinc-500">Sedang mempelajari pola Anda — ketuk untuk lengkapi data.</p>
        ) : (
          <>
            <p className="mt-0.5 text-xs font-semibold" style={{ color: score.color === "emerald" ? "#059669" : score.color === "amber" ? "#d97706" : "#e11d48" }}>
              {score.label}
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">{end >= 0 ? "Perkiraan sisa akhir bulan" : "Proyeksi defisit akhir bulan"}</p>
            <p className={`font-mono text-sm font-bold ${end >= 0 ? "text-zinc-900" : "text-rose-600"}`}>
              {formatCompact(Math.abs(end))}
            </p>
          </>
        )}
      </div>
      <ArrowRight className="h-5 w-5 shrink-0 text-zinc-300" />
    </button>
  );
}
